import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, resolveImg, variantWidth } from "@/lib/api";
import { formatCZK, formatCZKCompact, formatDate, plur } from "@/lib/format";
import { StatCard } from "@/components/shared/PagePrimitives";
import { StatusBadge } from "@/components/StatusBadge";
import { InvestorEditDialog } from "@/components/InvestorEditDialog";
import { AssignProjectsSheet } from "@/components/AssignProjectsSheet";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Building2, User, Mail, Phone, FileText, Briefcase, Coins, ExternalLink, Pencil, Trash2, MapPin, StickyNote, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";

// Řádek kontaktu (ikona + klikatelná hodnota). Skryje se, když hodnota chybí.
function ContactRow({ icon: Icon, label, value, href }) {
  if (!value) return null;
  const inner = <span className="font-semibold break-words">{value}</span>;
  return (
    <div className="flex items-start gap-2.5 text-sm">
      <Icon size={15} className="text-muted-foreground shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        {href ? <a href={href} className="text-brand hover:underline break-words">{value}</a> : inner}
      </div>
    </div>
  );
}

export default function InvestorDetail() {
  const { iid } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/investors/${iid}`).then((r) => setInv(r.data)).catch(() => { toast.error("Investor nenalezen."); nav("/investori"); });
  }, [iid, nav]);
  useEffect(() => { load(); }, [load]);

  if (!inv) return <div className="text-muted-foreground">Načítám…</div>;
  const s = inv.stats || {};
  const isCompany = inv.type !== "person";

  const summary = s.count > 0
    ? `${plur(s.count, "zakázka", "zakázky", "zakázek")}${s.first_date ? ` · první od ${formatDate(s.first_date)}` : ""}`
    : "Zatím bez zakázky";
  const headline = [inv.contact_person, summary].filter(Boolean).join(" · ");

  const remove = async () => {
    if (!await ask(`Smazat investora „${inv.name}"? Zakázky zůstanou, jen se odpojí.`)) return;
    try { await api.delete(`/investors/${iid}`); toast.success("Investor smazán."); nav("/investori"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const detach = async (p) => {
    if (!await ask(`Odpojit zakázku „${p.name}" od investora? Zakázka zůstane, jen bude bez investora.`)) return;
    try {
      const { data } = await api.delete(`/investors/${iid}/projects/${p.id}`);
      setInv(data);
      toast.success("Zakázka odpojena.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="investor-detail-page">
      <Link to="/investori" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Zpět na investory</Link>

      {/* Hlavička */}
      <div className="bg-card border border-border rounded-[20px] p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
          {isCompany ? <Building2 size={26} /> : <User size={26} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-black tracking-tight truncate">{inv.name}</h1>
            <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground bg-secondary rounded-md px-2 py-0.5">{isCompany ? "Firma" : "Osoba"}</span>
          </div>
          {headline && <div className="text-sm text-muted-foreground mt-0.5 truncate">{headline}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="investor-edit-btn" className="h-10"><Pencil size={15} /> Upravit</Button>
          <Button variant="ghost" onClick={remove} data-testid="investor-delete-btn" className="h-10 text-destructive hover:text-destructive"><Trash2 size={15} /></Button>
        </div>
      </div>

      {/* Statistiky spolupráce */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Briefcase} label="Zakázky celkem" value={s.count ?? 0} testid="investor-stat-count" />
        <StatCard icon={Briefcase} label="Aktivní" value={s.active ?? 0} brand testid="investor-stat-active" />
        <StatCard icon={Coins} label="Nasmlouváno" value={formatCZKCompact(s.total_contract || 0)} title={formatCZK(s.total_contract || 0)} testid="investor-stat-contract" />
        <StatCard icon={FileText} label="Spolupráce od" value={s.first_date ? formatDate(s.first_date) : "—"} testid="investor-stat-since" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Levý sloupec: kontakt + poznámka */}
        <div className="flex flex-col gap-5 min-w-0 md:col-span-1">
          <div className="bg-card border border-border rounded-[20px] p-5 space-y-3">
            <h2 className="font-bold flex items-center gap-2"><User size={16} className="text-brand" /> Kontakt & údaje</h2>
            <ContactRow icon={User} label="Kontaktní osoba" value={inv.contact_person} />
            <ContactRow icon={Mail} label="E-mail" value={inv.email} href={inv.email ? `mailto:${inv.email}` : null} />
            <ContactRow icon={Phone} label="Telefon" value={inv.phone} href={inv.phone ? `tel:${inv.phone.replace(/\s/g, "")}` : null} />
            <ContactRow icon={MapPin} label="Fakturační adresa" value={inv.address} />
            {inv.ico && (
              <div className="flex items-start gap-2.5 text-sm">
                <Building2 size={15} className="text-muted-foreground shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">IČO</div>
                  <a href={`https://ares.gov.cz/ekonomicke-subjekty?ico=${inv.ico}`} target="_blank" rel="noopener noreferrer"
                     className="text-brand hover:underline inline-flex items-center gap-1 font-mono">{inv.ico} <ExternalLink size={12} /></a>
                </div>
              </div>
            )}
            <ContactRow icon={FileText} label="DIČ" value={inv.dic} />
            {!inv.contact_person && !inv.email && !inv.phone && !inv.ico && !inv.dic && !inv.address && (
              <p className="text-sm text-muted-foreground">Žádné kontaktní údaje. Doplňte je tlačítkem Upravit.</p>
            )}
          </div>

          {inv.note && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-[20px] p-5 space-y-2.5" data-testid="investor-note">
              <h2 className="font-bold flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <StickyNote size={16} /> Poznámka
              </h2>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{inv.note}</p>
            </div>
          )}
        </div>

        {/* Zakázky s investorem */}
        <div className="bg-card border border-border rounded-[20px] p-5 md:col-span-2">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h2 className="font-bold flex items-center gap-2"><Briefcase size={16} className="text-brand" /> Zakázky s tímto investorem ({s.count ?? 0})</h2>
            <Button onClick={() => setAssignOpen(true)} data-testid="assign-open" className="h-10 font-bold w-full sm:w-auto"><Plus size={16} /> Přiřadit zakázku</Button>
          </div>
          {(inv.projects || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Zatím žádná zakázka. Přiřaď mu existující tlačítkem výš, nebo ho vyber při zakládání nové.</p>
          ) : (
            <div className="space-y-2" data-testid="investor-projects">
              {inv.projects.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3 rounded-[14px] border border-border transition-colors hover:border-brand">
                  <button onClick={() => nav(`/zakazky/${p.id}`)} data-testid={`investor-project-${p.id}`}
                    className="flex items-center gap-3 min-w-0 flex-1 text-left">
                    <div className="w-11 h-11 rounded-lg bg-secondary bg-cover bg-center shrink-0"
                      style={{ backgroundImage: `url('${resolveImg(p.cover_photo, variantWidth(44)) || "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&q=85&w=200"}')` }} />
                    <div className="min-w-0 flex-1">
                      <div className="font-bold truncate">{p.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {p.code ? `${p.code} · ` : ""}{p.start_date ? formatDate(p.start_date) : "—"}
                        {p.end_date ? ` – ${formatDate(p.end_date)}` : ""}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <StatusBadge status={p.status} />
                      {p.contract_price ? <span className="text-xs font-semibold tabular-nums">{formatCZK(p.contract_price)}</span> : null}
                    </div>
                  </button>
                  <Button variant="outline" size="icon" onClick={() => detach(p)} title="Odpojit od investora"
                    data-testid={`investor-detach-${p.id}`}
                    className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive hover:border-destructive">
                    <X size={15} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <InvestorEditDialog open={editOpen} onOpenChange={setEditOpen} mode="edit" investor={inv} onSaved={load} />
      <AssignProjectsSheet investorId={iid} open={assignOpen} onOpenChange={setAssignOpen} onAssigned={setInv} />
    </div>
  );
}
